import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './style.scss';
import PropTypes from 'prop-types';
import List from '@material-ui/core/List';
import AppointmentDetails from 'components/HealthRecords/Appointments/AppointmentDetails';
import SelectableListItem from 'components/Utils/SelectableListItem';
import AppointmentBadge from 'components/HealthRecords/Appointments/AppointmentBadge';
import NewAppointment from 'components/HealthRecords/Appointments/NewAppointment';
import AppointmentUtils from 'services/AppointmentUtils';
import _ from 'lodash';
import DefaultButton from 'components/Utils/NewDefaultButton';
import PerformanceUtils from 'services/PerformanceUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppSelector } from 'careand-redux/store';
import ResponsiveColumns from 'components/ResponsiveColumns';

const NEW_APPOINTMENT_KEY = 'new-appointment';

const AppointmentList = ({
  patient,
  allowClose,
  allowNewAppointment,
  allowEdit,
  openAppointmentId,
  bookedBy,
  sendMessage,
  showCalendar,
}) => {
  const dispatch = useAppDispatch();
  const patientId = patient.id;
  const navigateTo = useRef();

  const patientAppointments = useAppSelector((state) => state.appointments.byId[patientId]);

  const [limit, setLimit] = useState(25);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState();

  const onAppointmentSelected = useCallback((id) => {
    setSelectedAppointmentId(id);
    setTimeout(() => {
      navigateTo.current?.(id === '' ? 0 : 1);
    }, 0);
  }, []);

  useEffect(() => {
    dispatch(api.Appointments.getUserAppointments({ userId: patientId, role: 'patient' }));
  }, [dispatch, patientId]);

  useEffect(() => {
    if (openAppointmentId && openAppointmentId.startsWith(NEW_APPOINTMENT_KEY)) {
      onAppointmentSelected(NEW_APPOINTMENT_KEY);
    }
  }, [openAppointmentId]);

  const appointments = useMemo(() => {
    let all = PerformanceUtils.sortBy(patientAppointments || []).splice(0, limit) || [];

    let confirmed = filterByStatus(all, [
      AppointmentUtils.getAppointmentStatus().scheduled,
      AppointmentUtils.getAppointmentStatus().checked_in,
    ]);

    let past = AppointmentUtils.getPastAppointments(all);

    return { past, confirmed, all };
  }, [patientAppointments, limit]);

  const selectedAppointment = useMemo(() => {
    return appointments.all.filter((appointment) => appointment.id === selectedAppointmentId)[0];
  }, [appointments, selectedAppointmentId]);

  return (
    <div className="row full-width full-height appointment-list-component">
      <ResponsiveColumns
        navigateTo={navigateTo}
        columns={[
          {
            name: 'List',
            element: (
              <div className="full-height-panel underside-right-panel scroll-list list">
                <DefaultButton
                  name="Book New Appointment"
                  onClick={() => {
                    onAppointmentSelected(NEW_APPOINTMENT_KEY);
                    setTimeout(() => {
                      navigateTo.current?.(1);
                    });
                  }}
                  style={{ width: '85%', margin: '30px 20px 0px 20px' }}
                  hide={!allowNewAppointment}
                />
                <ListContent
                  list={appointments.confirmed}
                  title="Scheduled Appointments"
                  onClick={onAppointmentSelected}
                  selectedId={selectedAppointmentId}
                />
                <ListContent
                  list={appointments.past}
                  title="Past Appointments"
                  onClick={onAppointmentSelected}
                  selectedId={selectedAppointmentId}
                />
                <div className="row" style={{ paddingBottom: '10px' }}>
                  <DefaultButton
                    name="Load more"
                    style={{ margin: 'auto' }}
                    onClick={() => setLimit(limit + 25)}
                    hide={(patientAppointments || []).length < limit}
                  />
                </div>
              </div>
            ),
          },
          selectedAppointmentId === NEW_APPOINTMENT_KEY
            ? {
                name: 'New Appointment',
                element: (
                  <NewAppointment
                    key={openAppointmentId || 'new-appointment'}
                    patient={patient}
                    bookedBy={bookedBy}
                    allowClose={true}
                    onClose={() => onAppointmentSelected('')}
                    sendMessage={sendMessage}
                  />
                ),
              }
            : undefined,
          selectedAppointment !== undefined
            ? {
                name: 'Appointment',
                element: (
                  <AppointmentDetails
                    key={selectedAppointmentId}
                    appointment={selectedAppointment}
                    onClose={() => onAppointmentSelected('')}
                    allowEdit={allowEdit}
                    allowClose={allowClose}
                    showCalendar={showCalendar}
                  />
                ),
              }
            : undefined,
        ]}
      />
    </div>
  );

  function filterByStatus(appointments, statuses) {
    let result = appointments.filter((appointment) => {
      let match = false;
      statuses.forEach((status) => {
        if (appointment.status === status) match = true;
      });
      return match;
    });
    result = _.sortBy(result, 'date');
    result = _.reverse(result);
    return result;
  }
};

const ListContent = ({ list = [], title, selectedId, onClick }) =>
  list.length > 0 && (
    <div>
      <span className="small-title padded-title">{title}</span>
      <List>
        {list.map((appointment) => (
          <SelectableListItem
            key={appointment.id}
            selected={selectedId === appointment.id}
            item={<AppointmentBadge key={appointment.id} appointment={appointment} />}
            onClick={() => onClick(appointment.id)}
          />
        ))}
      </List>
    </div>
  );

AppointmentList.propTypes = {
  patient: PropTypes.object.isRequired,
  bookedBy: PropTypes.object,
  allowClose: PropTypes.bool,
  allowEdit: PropTypes.bool,
  sendMessage: PropTypes.func,
  allowNewAppointment: PropTypes.bool,
  activeTab: PropTypes.object,
  openAppointmentId: PropTypes.string,
};

export default AppointmentList;
